import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import ChecklistItem from './ChecklistItem';
import ProgressRing from './ProgressRing';
import {
	checklistSectionStyles,
	containerStyles,
	footerStyles,
	getButtonStyles,
	headerIconStyles,
	headerStyles,
	overlayStyles,
	progressCountStyles,
	progressSectionStyles,
	subtitleStyles,
	titleStyles,
} from './styled';

interface ReadinessChecklistProps {
	isOpen: boolean;
	onComplete: () => void;
}

const ShieldIcon = () => (
	<svg
		width='26'
		height='26'
		viewBox='0 0 24 24'
		fill='none'
		stroke='#ffffff'
		strokeWidth='1.8'
		strokeLinecap='round'
		strokeLinejoin='round'
	>
		<path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />
		<path d='M9 12l2 2 4-4' />
	</svg>
);

const DeviceIcon = () => (
	<svg
		width='20'
		height='20'
		viewBox='0 0 24 24'
		fill='none'
		stroke='#ffffff'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
	>
		<rect
			x='5'
			y='2'
			width='14'
			height='20'
			rx='2'
			ry='2'
		/>
		<line
			x1='12'
			y1='18'
			x2='12.01'
			y2='18'
		/>
	</svg>
);

const IdCardIcon = () => (
	<svg
		width='20'
		height='20'
		viewBox='0 0 24 24'
		fill='none'
		stroke='#ffffff'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
	>
		<rect
			x='2'
			y='5'
			width='20'
			height='14'
			rx='2'
		/>
		<line
			x1='2'
			y1='10'
			x2='22'
			y2='10'
		/>
		<circle
			cx='8'
			cy='15'
			r='1.5'
		/>
		<line
			x1='13'
			y1='13'
			x2='18'
			y2='13'
		/>
		<line
			x1='13'
			y1='16'
			x2='16'
			y2='16'
		/>
	</svg>
);

const CameraIcon = () => (
	<svg
		width='20'
		height='20'
		viewBox='0 0 24 24'
		fill='none'
		stroke='#ffffff'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
	>
		<path d='M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z' />
		<circle
			cx='12'
			cy='13'
			r='4'
		/>
	</svg>
);

const LocationIcon = () => (
	<svg
		width='20'
		height='20'
		viewBox='0 0 24 24'
		fill='none'
		stroke='#ffffff'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
	>
		<path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z' />
		<circle
			cx='12'
			cy='10'
			r='3'
		/>
	</svg>
);

const HashIcon = () => (
	<svg
		width='20'
		height='20'
		viewBox='0 0 24 24'
		fill='none'
		stroke='#ffffff'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
	>
		<line
			x1='4'
			y1='9'
			x2='20'
			y2='9'
		/>
		<line
			x1='4'
			y1='15'
			x2='20'
			y2='15'
		/>
		<line
			x1='10'
			y1='3'
			x2='8'
			y2='21'
		/>
		<line
			x1='16'
			y1='3'
			x2='14'
			y2='21'
		/>
	</svg>
);

const ArrowIcon = () => (
	<svg
		width='16'
		height='16'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2.5'
		strokeLinecap='round'
		strokeLinejoin='round'
	>
		<path d='M5 12h14' />
		<path d='m12 5 7 7-7 7' />
	</svg>
);

const CHECKLIST_ITEMS = [
	{
		icon: <DeviceIcon />,
		title: 'Correct Device',
		description: 'Ensure this is the device you will be using for the job. You will need it during your assignment.',
	},
	{
		icon: <IdCardIcon />,
		title: 'ID Documents Available',
		description: 'Have your Government-issued ID and Voters Card ID ready on this device for upload.',
	},
	{
		icon: <CameraIcon />,
		title: 'Selfie Required',
		description: 'You will be required to take a live photo of yourself using your device camera.',
	},
	{
		icon: <LocationIcon />,
		title: 'At Your Assigned Location',
		description:
			'You must be at a location in close proximity to the Ward, PU or LGA you are applying for. Your position will be verified via GPS.',
	},
	{
		icon: <HashIcon />,
		title: 'Know Your Codes',
		description: 'You know your PU, Ward and LGA Codes.',
	},
];

const ReadinessChecklist: React.FC<ReadinessChecklistProps> = ({ isOpen, onComplete }) => {
	const [checkedItems, setCheckedItems] = useState<boolean[]>(new Array(CHECKLIST_ITEMS.length).fill(false));
	const containerRef = useRef<HTMLDivElement>(null);

	const confirmedCount = checkedItems.filter(Boolean).length;
	const allChecked = confirmedCount === CHECKLIST_ITEMS.length;

	const toggleItem = useCallback((index: number) => {
		setCheckedItems((prev) => {
			const next = [...prev];
			next[index] = !next[index];
			return next;
		});
	}, []);

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && containerRef.current) {
			containerRef.current.focus();
		}
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Tab') {
				const focusable = containerRef.current?.querySelectorAll<HTMLElement>('[role="checkbox"], button:not(:disabled)');
				if (!focusable || focusable.length === 0) return;

				const first = focusable[0];
				const last = focusable[focusable.length - 1];

				if (e.shiftKey && document.activeElement === first) {
					e.preventDefault();
					last.focus();
				} else if (!e.shiftKey && document.activeElement === last) {
					e.preventDefault();
					first.focus();
				}
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isOpen]);

	if (!isOpen) return null;

	return (
		<div style={overlayStyles}>
			<div
				ref={containerRef}
				role='dialog'
				aria-modal='true'
				aria-labelledby='readiness-title'
				tabIndex={-1}
				style={containerStyles}
			>
				{/* Header */}
				<div style={headerStyles}>
					<div style={headerIconStyles}>
						<ShieldIcon />
					</div>
					<h2
						id='readiness-title'
						style={titleStyles}
					>
						Before You Begin
					</h2>
					<p style={subtitleStyles}>Please confirm you have the following ready before proceeding</p>
				</div>

				{/* Progress Ring */}
				<div style={progressSectionStyles}>
					<ProgressRing
						confirmed={confirmedCount}
						total={CHECKLIST_ITEMS.length}
					/>
					<span style={progressCountStyles}>
						{confirmedCount} of {CHECKLIST_ITEMS.length} confirmed
					</span>
				</div>

				{/* Checklist Items */}
				<div style={checklistSectionStyles}>
					{CHECKLIST_ITEMS.map((item, index) => (
						<ChecklistItem
							key={item.title}
							icon={item.icon}
							title={item.title}
							description={item.description}
							isChecked={checkedItems[index]}
							onToggle={() => toggleItem(index)}
							delay={200 + index * 80}
						/>
					))}
				</div>

				{/* Footer Button */}
				<div style={footerStyles}>
					<button
						type='button'
						disabled={!allChecked}
						style={{
							...getButtonStyles(allChecked),
							animation: 'checklistSlideUp 400ms cubic-bezier(0.22, 1, 0.36, 1) 650ms both',
						}}
						onClick={onComplete}
						aria-label="I'm Ready, Let's Begin"
					>
						I&apos;m Ready, Let&apos;s Begin
						<ArrowIcon />
					</button>
				</div>
			</div>
		</div>
	);
};

export default ReadinessChecklist;
